import { useState, useEffect, useRef, useCallback } from 'react'
import { useContentFStore } from '@/stores/useContentFStore'
import { ContentFFoolscapContent } from './ContentFFoolscapContent'
import { ContentFCardsPanel } from './ContentFCardsPanel'
import { FileText, Library, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'

type TabType = 'document' | 'cards'

export function ContentFDocumentPanel() {
  const {
    foolscapMarkdown,
    foolscapMarkdownAnnotated,
    showAnnotations,
    toggleAnnotations,
    phase,
    integratedCardIds,
    surfacedCards,
    workflowStatus,
    sessionId,
    setSelectedCardId,
  } = useContentFStore()
  const isStreaming = workflowStatus === 'streaming'
  const [activeTab, setActiveTab] = useState<TabType>('document')

  // Track user tab switches to avoid auto-switching back
  const userSwitchedTabRef = useRef(false)

  // Handle manual tab clicks
  const handleTabClick = (tab: TabType) => {
    userSwitchedTabRef.current = true
    setActiveTab(tab)
  }

  // Reset to document tab when session changes
  useEffect(() => {
    setActiveTab('document')
    userSwitchedTabRef.current = false
  }, [sessionId])

  // Handle card click from document - switch to cards tab and select the card
  const handleCardClick = useCallback((cardId: string) => {
    setSelectedCardId(cardId)
    setActiveTab('cards')
    userSwitchedTabRef.current = true
  }, [setSelectedCardId])

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-1">
          {/* Document tab */}
          <button
            onClick={() => handleTabClick('document')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              activeTab === 'document'
                ? 'bg-muted/50 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Document</span>
          </button>

          {/* Cards tab */}
          <button
            onClick={() => handleTabClick('cards')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
              activeTab === 'cards'
                ? 'bg-muted/50 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            )}
          >
            <Library className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Cards</span>
            {surfacedCards.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-muted rounded-full text-xs">
                {surfacedCards.length}
              </span>
            )}
            {integratedCardIds.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full text-xs">
                {integratedCardIds.length}
              </span>
            )}
          </button>
        </div>

        {/* Phase indicator and annotations toggle */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {/* Annotations toggle - only show if there are annotations */}
          {foolscapMarkdownAnnotated && (
            <button
              onClick={toggleAnnotations}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 rounded transition-colors',
                showAnnotations
                  ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                  : 'bg-muted hover:bg-muted/70'
              )}
              title={showAnnotations ? 'Hide changes' : 'Show changes'}
            >
              {showAnnotations ? (
                <Eye className="w-3 h-3" />
              ) : (
                <EyeOff className="w-3 h-3" />
              )}
              <span>Changes</span>
            </button>
          )}
          <span className="px-2 py-0.5 bg-muted rounded-full capitalize">
            {phase}
          </span>
        </div>
      </div>

      {/* Content - always mount both to preserve state, hide with CSS */}
      <div className="flex-1 overflow-hidden relative">
        <div className={cn('absolute inset-0', activeTab === 'document' ? '' : 'invisible pointer-events-none')}>
          <div className="h-full overflow-y-auto">
            {foolscapMarkdown ? (
              <ContentFFoolscapContent
                foolscapMarkdown={foolscapMarkdown}
                foolscapMarkdownAnnotated={foolscapMarkdownAnnotated}
                showAnnotations={showAnnotations}
                streaming={isStreaming}
                onCardClick={handleCardClick}
              />
            ) : (
              <div className="h-full flex items-center justify-center px-6 text-center">
                <div className="text-muted-foreground text-sm max-w-sm space-y-2">
                  <p className="font-medium text-foreground">Your Foolscap Document</p>
                  <p>
                    As we discuss your story, I'll build out your one-page structure here.
                  </p>
                  <p className="text-xs">
                    Start chatting to begin building your foolscap.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={cn('absolute inset-0', activeTab === 'cards' ? '' : 'invisible pointer-events-none')}>
          <ContentFCardsPanel />
        </div>
      </div>
    </div>
  )
}
